import { TriangleAlert } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Blueprint } from '@/components/ui/Blueprint';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { ptBR } from '@/i18n/pt-BR';

export interface ServerErrorPageProps {
  /** Identificador opaco da falha (cabeçalho `x-correlation-id` ou similar). */
  correlationId?: string | null;
  /** Handler opcional para botão "Tentar novamente". Se omitido, botão não aparece. */
  onRetry?: () => void;
}

export function ServerErrorPage({ correlationId, onRetry }: ServerErrorPageProps = {}) {
  const t = ptBR.errorPages.serverError;
  return (
    <section
      data-testid="server-error-page"
      aria-labelledby="server-error-title"
      className="mx-auto max-w-2xl py-16"
    >
      <Blueprint className="px-8 py-10">
        <p
          data-testid="server-error-code"
          className="mb-2 text-center font-mono text-caption uppercase tracking-[0.02em] text-fg-dim"
        >
          {t.code}
        </p>
        <EmptyState
          icon={TriangleAlert}
          title={t.title}
          description={t.body}
          action={
            <div className="flex flex-wrap items-center justify-center gap-2">
              {onRetry ? (
                <Button
                  variant="secondary"
                  size="default"
                  onClick={onRetry}
                  data-testid="server-error-retry-cta"
                >
                  {t.retryCta}
                </Button>
              ) : null}
              <Button asChild variant="primary" size="default">
                <Link to="/" data-testid="server-error-back-cta">
                  {t.backCta}
                </Link>
              </Button>
            </div>
          }
          aria-labelledby="server-error-title"
          className="border-0 p-0"
        />
      </Blueprint>
      <h1 id="server-error-title" className="sr-only">
        {t.title}
      </h1>
      {correlationId != null && correlationId.length > 0 ? (
        <p
          data-testid="server-error-correlation"
          className="mt-4 text-center font-mono text-caption text-fg-dim"
        >
          {t.correlationLabel}:{' '}
          <span data-testid="server-error-correlation-value">{correlationId}</span>
        </p>
      ) : null}
    </section>
  );
}
